import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Box, Button, Collapse, Grid, IconButton, Skeleton, TextField, Typography, useMediaQuery } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { IUser, IUserNote, QueryKey } from 'common-types';
import { SingleNote } from 'components/single-note';
import { getUnixTime } from 'date-fns';
import { useGetUserData } from 'hooks/use-get-user-data';
import { useSetUserData } from 'hooks/use-set-user-data';
import theme from 'providers/theme/theme';
import { ChangeEvent, memo, useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useAnalytics } from 'services/analytics';
import { MAX_NOTES_COUNT } from 'utils';

import TrashIcon from './assets/trash-icon.svg?react';
import {
  cancelButtonStyles,
  deleteButtonSx,
  getAddNoteIconSx,
  getFloatingLabelSx,
  greyGradientSx,
  inputContainerSx,
  limeGradientSx,
  notesContainerStyles,
  notesExpandedHeadingSx,
  noteTextFieldSx,
  saveButtonStyles,
} from './styles';

const NOTE_TITLE = 'Note';

export const Notes = memo(() => {
  const { trackEvent } = useAnalytics();
  const queryClient = useQueryClient();
  const { userNotes, isSuccess } = useGetUserData();
  const { mutateAsync, isError } = useSetUserData();

  const [isAddNoteExpanded, setIsAddNoteExpanded] = useState<boolean>(false);
  const [isNotesExpanded, setIsNotesExpanded] = useState<boolean>(false);
  const [noteEditingIndex, setNoteEditingIndex] = useState<number>(null);
  const [userNoteText, setUserNoteText] = useState<string>('');
  const [isTextFieldFocused, setIsTextFieldFocused] = useState<boolean>(false);

  const isMediumAndDown = useMediaQuery(theme.breakpoints.down('lg'));
  const isLabelSmall = isTextFieldFocused || userNoteText.length > 0;

  const handleAddNote = useCallback(() => {
    trackEvent('notes_created');
    setIsAddNoteExpanded(false);
    setNoteEditingIndex(null);
    setUserNoteText('');

    void mutateAsync({
      notes: [{ title: NOTE_TITLE, text: userNoteText, timestamp: getUnixTime(Date.now()) }, ...userNotes],
    });

    queryClient.setQueryData([QueryKey.USER_DATA], (prev: IUser) => {
      return {
        ...prev,
        notes: [{ title: NOTE_TITLE, text: userNoteText, timestamp: getUnixTime(Date.now()) }, ...userNotes],
      };
    });

    void queryClient.invalidateQueries({ queryKey: [QueryKey.USER_DATA, 'notes'] });
  }, [userNoteText, mutateAsync, queryClient]);

  const handleSaveNote = useCallback(() => {
    mutateAsync({
      notes: [
        ...userNotes.slice(0, noteEditingIndex),
        { title: NOTE_TITLE, text: userNoteText, timestamp: getUnixTime(Date.now()) },
        ...userNotes.slice(noteEditingIndex + 1),
      ],
    }).then(() => {
      setNoteEditingIndex(null);
    });
    setIsAddNoteExpanded(false);

    queryClient.setQueryData([QueryKey.USER_DATA], (prev: IUser) => {
      return {
        ...prev,
        notes: [
          ...prev.notes.slice(0, noteEditingIndex),
          { title: NOTE_TITLE, text: userNoteText, timestamp: getUnixTime(Date.now()) },
          ...prev.notes.slice(noteEditingIndex + 1),
        ],
      };
    });

    void queryClient.invalidateQueries({ queryKey: [QueryKey.USER_DATA, 'notes'] });
  }, [userNoteText, noteEditingIndex, mutateAsync, queryClient]);

  const handleDeleteNote = useCallback(() => {
    if (noteEditingIndex === null) return;
    setIsAddNoteExpanded(false);
    setUserNoteText('');

    mutateAsync({
      notes: [...userNotes.slice(0, noteEditingIndex), ...userNotes.slice(noteEditingIndex + 1)],
    }).then(() => {
      setNoteEditingIndex(null);
    });

    queryClient.setQueryData([QueryKey.USER_DATA], (prev: IUser) => {
      return {
        ...prev,
        notes: [...prev.notes.slice(0, noteEditingIndex), ...prev.notes.slice(noteEditingIndex + 1)],
      };
    });

    void queryClient.invalidateQueries({ queryKey: [QueryKey.USER_DATA, 'notes'] });
  }, [noteEditingIndex, mutateAsync, queryClient]);

  const handleClickToEditNote = useCallback((note: Omit<IUserNote, 'timestamp'>, index: number) => {
    setIsAddNoteExpanded(true);
    setNoteEditingIndex(index);
    setUserNoteText(note.text);
  }, []);

  const handleClickCancelButton = useCallback(() => {
    setIsAddNoteExpanded(false);
    setNoteEditingIndex(null);
    setUserNoteText('');
  }, []);

  const handleClickAddNote = useCallback(() => {
    setIsAddNoteExpanded(true);
    setNoteEditingIndex(null);
    setUserNoteText('');
  }, []);

  const handleExpandNotesClick = (): void => {
    trackEvent('notes_tap');
    setIsNotesExpanded(!isNotesExpanded);
  };

  useEffect(() => {
    if (isError) {
      toast.error('Something went wrong. Please try again later.');
    }
  }, [isError]);

  return (
    <Box
      width="100%"
      borderRadius={4}
      px={6}
      py={5}
      position={{ xs: 'absolute', lg: 'static' }}
      sx={isAddNoteExpanded ? limeGradientSx : greyGradientSx}
    >
      {isSuccess ? (
        <>
          <Collapse in={!isAddNoteExpanded}>
            <Grid container justifyContent="space-between" alignItems="center">
              <Grid>
                <Typography fontWeight={500} fontSize={15} lineHeight="22px">
                  Notes
                </Typography>
              </Grid>
              <Grid>
                <Grid container alignItems="center">
                  <Grid>
                    <IconButton
                      size="small"
                      disabled={userNotes?.length >= MAX_NOTES_COUNT}
                      onClick={handleClickAddNote}
                    >
                      <Box component="span" sx={getAddNoteIconSx(userNotes?.length === MAX_NOTES_COUNT)}>
                        +
                      </Box>
                    </IconButton>
                  </Grid>
                  {!isMediumAndDown && (
                    <Grid sx={{ ml: 4 }}>
                      {userNotes?.length > 0 && (
                        <IconButton size="small" onClick={handleExpandNotesClick}>
                          {isNotesExpanded ? (
                            <KeyboardArrowUpIcon fontSize="small" />
                          ) : (
                            <KeyboardArrowDownIcon fontSize="small" />
                          )}
                        </IconButton>
                      )}
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
            <Collapse in={isMediumAndDown ? true : isNotesExpanded}>
              <Box maxHeight={{ xs: 'calc(41vh - 64px)', lg: 400 }} mt={4} sx={notesContainerStyles}>
                <Box display="flex" flexDirection="column" gap={4}>
                  {userNotes?.map(({ title, text, timestamp }, index) => (
                    <SingleNote
                      key={title + timestamp}
                      handleClickToEditNote={handleClickToEditNote}
                      index={index}
                      timestamp={timestamp}
                      title={title}
                      text={text}
                    />
                  ))}
                </Box>
              </Box>
            </Collapse>
          </Collapse>
          <Collapse in={isAddNoteExpanded}>
            <>
              <Box>
                <Typography sx={notesExpandedHeadingSx}>Notes</Typography>
                <Box sx={inputContainerSx}>
                  <Typography sx={getFloatingLabelSx(isLabelSmall)}>Share your thoughts</Typography>
                  <TextField
                    value={userNoteText}
                    onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void =>
                      setUserNoteText(e.target.value)
                    }
                    onFocus={(): void => setIsTextFieldFocused(true)}
                    onBlur={(): void => setIsTextFieldFocused(false)}
                    fullWidth
                    multiline
                    sx={noteTextFieldSx}
                  />
                </Box>
                <Grid container justifyContent="space-between" alignItems="center">
                  <Grid>
                    <Grid container alignItems="center" gap={4}>
                      <Grid>
                        <Button
                          disabled={userNoteText?.length === 0 || userNoteText === userNotes[noteEditingIndex]?.text}
                          onClick={noteEditingIndex !== null ? handleSaveNote : handleAddNote}
                          sx={saveButtonStyles}
                        >
                          Save
                        </Button>
                      </Grid>
                      <Grid>
                        <Button onClick={handleClickCancelButton} sx={cancelButtonStyles}>
                          Cancel
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid>
                    <IconButton onClick={handleDeleteNote} disabled={noteEditingIndex === null} sx={deleteButtonSx}>
                      <TrashIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              </Box>
            </>
          </Collapse>
        </>
      ) : (
        <Skeleton variant="rounded" animation="wave" height={34} />
      )}
    </Box>
  );
});
